import { Field, Form, useFormikContext } from 'formik';

import { useTranslation } from '@packlink/translation-provider';
import { ICountry, IPostalCode } from '@packlink/packlink-sdk';
import { Button, ButtonColor, ButtonVariant, Divider, Typography } from '@shipengine/giger';
import { FormField, Input } from '@shipengine/formik-giger';
import {
    PostalCodeOption,
    CountryOption,
    FormikCountrySelector,
    FormikPostalCodeSelector,
} from '@packlink/address-selectors';
import { PlaceOrigin } from '@types';
import { Notification, NotificationType } from '@components';
import { getDividerAddressFormStyles, getCancelButtonAddressForm } from './AddressFormStyles';
import { AddressFormValues } from './AddressForm';

interface AddressFormFieldsProps {
    /** Type of origin for this address. Either 'from' or 'to' */
    origin?: PlaceOrigin;
    /** Form title */
    title: string;
    /** State for alpha2Code */
    selectedAlpha2Code: string;
    /** On cancel success callback to be executed by the caller */
    onCancel(): void;
    /** State handler for postal code */
    setSelectedPostalCode(postalCode: IPostalCode | null): void;
    /** State handler for alpha2Code */
    setSelectedAlpha2Code(alpha2Code: string): void;
}

export const AddressFormFields = ({
    origin,
    title,
    selectedAlpha2Code,
    onCancel,
    setSelectedPostalCode,
    setSelectedAlpha2Code,
}: AddressFormFieldsProps): JSX.Element => {
    const { t } = useTranslation();
    const { setFieldValue, values, errors, isSubmitting } = useFormikContext<AddressFormValues>();

    /**
     * Set Country initial value.
     */
    const handleCountryLoad = (initialCountry: ICountry | undefined) => {
        if (initialCountry) {
            const countryOption = { label: initialCountry.name, value: initialCountry.alpha2Code };
            setFieldValue('country', countryOption);
            setSelectedAlpha2Code(initialCountry.alpha2Code);
        }
    };
    /**
     * Set PostalCode initial value.
     */
    const handlePostalCodeLoad = (initialPostalCode: IPostalCode | undefined) => {
        if (initialPostalCode) {
            setFieldValue('postalCode', {
                label: initialPostalCode.text,
                value: initialPostalCode.zipCode,
            });
            setSelectedPostalCode(initialPostalCode);
        }
    };
    /**
     * Handle Country change and update PostalCode.
     */
    const handleCountryChange = (_fieldName: string, optionSelected: CountryOption | null) => {
        setFieldValue('country', optionSelected, true);
        if (optionSelected?.value !== selectedAlpha2Code) {
            setFieldValue('postalCode', null, true);
            setFieldValue('street1', '', true);
            setFieldValue('street2', '', true);
            setSelectedPostalCode(null);
        }
        setSelectedAlpha2Code(optionSelected?.value || '');
    };
    /**
     * Handle PostalCode change.
     */
    const handlePostalCodeChange = (_fieldName: string, value: PostalCodeOption, postalCode: IPostalCode | null) => {
        setFieldValue('postalCode', value, true);
        setSelectedPostalCode(postalCode);
    };

    return (
        <Form>
            <Typography component="h4" variant="subtitle1">
                {title}
            </Typography>
            {origin === PlaceOrigin.TO && (
                <Notification inline={true} type={NotificationType.INFO}>
                    {t('address-form.section.disclaimer')}
                </Notification>
            )}
            <Typography component="p" variant="body1">
                * {t('address-form.section.mandatory-fields')}
            </Typography>
            <Field
                name="country"
                component={FormikCountrySelector}
                onLoad={handleCountryLoad}
                onChange={handleCountryChange}
            />
            <FormField name="firstName">
                <Field component={Input} label={`${t('address-form.field-first-name.label')} *`} />
            </FormField>
            <FormField name="lastName">
                <Field component={Input} label={`${t('address-form.field-last-name.label')} *`} />
            </FormField>
            <FormField name="company">
                <Field component={Input} label={t('address-form.field-company.label')} />
            </FormField>
            <FormField name="street1">
                <Field component={Input} label={`${t('address-form.field-address-line-1.label')} *`} />
            </FormField>
            <FormField name="street2">
                <Field component={Input} label={t('address-form.field-address-line-2.label')} />
            </FormField>
            <Field
                name="postalCode"
                component={FormikPostalCodeSelector}
                alpha2Code={values.country?.value}
                disabled={!selectedAlpha2Code}
                onChange={handlePostalCodeChange}
                onLoad={handlePostalCodeLoad}
            />
            <FormField name="email">
                <Field component={Input} label={`${t('address-form.field-email.label')} *`} type="email" />
            </FormField>
            <FormField name="phone">
                <Field component={Input} label={`${t('address-form.field-phone.label')} *`} type="phone" />
            </FormField>
            <Divider css={getDividerAddressFormStyles} />
            <Button color={ButtonColor.PRIMARY} type="submit" disabled={isSubmitting || !!Object.keys(errors).length}>
                {t('address-form.action.submit')}
            </Button>

            <Button
                variant={ButtonVariant.OUTLINED}
                color={ButtonColor.PRIMARY}
                onClick={onCancel}
                css={getCancelButtonAddressForm}
            >
                {t('address-form.action.close')}
            </Button>
        </Form>
    );
};
